import React from 'react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo
} from 'react';

import * as amplitude from '@amplitude/analytics-browser';
import { sessionReplayPlugin } from '@amplitude/plugin-session-replay-browser';

import { useAuth } from '@lucidhq/ui-auth';

import { AMPLITUDE_ENABLED, AMPLITUDE_KEY } from '~/constants';

export enum AMPLITUDE_EVENTS {
  PREVIEW_NEW_UI = 'preview_new_ui'
}

type TrackEventFn = (
  eventName: AMPLITUDE_EVENTS,
  params?: Record<string, any>
) => void;

type AmplitudeEvents = {
  trackEvent: TrackEventFn;
};

const AmplitudeContext = createContext<AmplitudeEvents>({} as AmplitudeEvents);

export const useAmplitude = () => useContext(AmplitudeContext);

export const AmplitudeProvider = ({
  children
}: {
  children: React.ReactNode;
}) => {
  const { user, isAuthenticated } = useAuth();

  useEffect(() => {
    if (!AMPLITUDE_KEY || !AMPLITUDE_ENABLED || !isAuthenticated || !user)
      return;

    const sessionReplayTracking = sessionReplayPlugin({
      sampleRate: 0.4
    });

    amplitude.add(sessionReplayTracking);

    amplitude.init(AMPLITUDE_KEY, user.email, {
      serverZone: 'EU',
      autocapture: {
        attribution: false,
        sessions: true,
        formInteractions: false,
        fileDownloads: false,
        pageViews: false
      }
    });
    const isInternalUser =
      user.email.endsWith('@cint.com') || user.email.endsWith('@luc.id');

    const identify = new amplitude.Identify();
    identify.set('is_internal', isInternalUser);
    identify.set('user_email', user.email);

    amplitude.identify(identify);
  }, [user, isAuthenticated]);

  const trackEvent = useCallback<TrackEventFn>((eventName, params = {}) => {
    if (AMPLITUDE_ENABLED) {
      amplitude.track(eventName, {
        ...params
      });
    }
  }, []);

  const value = useMemo<AmplitudeEvents>(() => ({ trackEvent }), [trackEvent]);

  return (
    <AmplitudeContext.Provider value={value}>
      {children}
    </AmplitudeContext.Provider>
  );
};
